import React, { useState, useEffect } from "react";
import {
  Save,
  Clock,
  MapPin,
  Loader2,
  Calendar,
  User,
  CheckCircle2,
} from "lucide-react";
import api from "../api/axiosInstance";

const MonthlyAttendanceEditor = () => {
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedEmpId, setSelectedEmpId] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7),
  );
  const [attendanceData, setAttendanceData] = useState({});
  const [daysInMonth, setDaysInMonth] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkSite, setBulkSite] = useState("");
  const [bulkValue, setBulkValue] = useState(1); // Default to 1 Shift or Present
  const [originalData, setOriginalData] = useState({});
  const fillMonth = () => {
    if (!bulkSite) {
      alert("Please select a Site first to fill the month.");
      return;
    }

    const newBatchData = {};
    daysInMonth.forEach((date) => {
      const isSunday = new Date(date).getDay() === 0;

      // Logic: Fill only if it's not a Sunday (optional, remove if you want to fill Sundays too)

      newBatchData[date] = {
        project_id: bulkSite,
        multiplier: parseFloat(bulkValue),
        status: bulkValue > 0 ? "Present" : "Absent",
        in_time: currentEmp.pay_type === "Hourly" ? "09:00:00" : "00:00:00",
        out_time: currentEmp.pay_type === "Hourly" ? "18:00:00" : "00:00:00",
      };
    });

    setAttendanceData((prev) => ({ ...prev, ...newBatchData }));
  };
  useEffect(() => {
    const fetchMeta = async () => {
      const [empRes, projRes] = await Promise.all([
        api.get("get_employees.php"),
        api.get("get_projects.php"),
      ]);
      setEmployees(empRes.data.employees || []);
      setProjects(projRes.data.projects || []);
    };
    fetchMeta();
  }, []);

  useEffect(() => {
    if (selectedEmpId && selectedMonth) {
      loadMonthlyLog();
    }
  }, [selectedEmpId, selectedMonth]);

  const loadMonthlyLog = async () => {
    setLoading(true);
    try {
      // Generate all dates for the chosen month
      const [year, month] = selectedMonth.split("-");
      const totalDays = new Date(year, month, 0).getDate();
      const dates = Array.from(
        { length: totalDays },
        (_, i) => `${selectedMonth}-${(i + 1).toString().padStart(2, "0")}`,
      );
      setDaysInMonth(dates);

      // Fetch existing logs from backend
      const res = await api.get(
        `get_employee_month_attendance.php?emp_id=${selectedEmpId}&month=${selectedMonth}`,
      );

      const mapped = {};
      res.data.attendance?.forEach((a) => {
        mapped[a.date] = {
          status: a.status,
          multiplier: parseFloat(a.multiplier) || 0,
          in_time: a.in_time || "",
          out_time: a.out_time || "",
          project_id: a.project_id,
        };
      });
      setAttendanceData(mapped);
      setOriginalData(JSON.parse(JSON.stringify(mapped))); // Deep copy
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateDayEntry = (date, field, value) => {
    const emp = employees.find((e) => e.id == selectedEmpId);
    setAttendanceData((prev) => ({
      ...prev,
      [date]: {
        ...(prev[date] || {
          status: "Present",
          multiplier: 1,
          project_id: emp?.assigned_site_id,
        }),
        [field]: value,
      },
    }));
  };

  const handleBulkSave = async () => {
    setSaving(true);
    try {
      const emp = employees.find((e) => e.id == selectedEmpId);
      const payload = daysInMonth.map((date) => {
        const data = attendanceData[date] || {
          status: "Absent",
          multiplier: 0,
        };
        return {
          date,
          employee_id: selectedEmpId,
          applied_rate: emp.rate,
          project_id: data.project_id || emp.assigned_site_id,
          ...data,
        };
      });

      await api.post("save_bulk_attendance_monthly.php", {
        employee_id: selectedEmpId,
        month: selectedMonth,
        attendance: payload,
      });
      alert("Monthly Logs Synced!");
    } catch (err) {
      alert("Error saving logs");
    } finally {
      setSaving(false);
    }
  };
  // 3. Revert Function
  const revertToOriginal = () => {
    if (window.confirm("Revert all unsaved changes for this month?")) {
      setAttendanceData(JSON.parse(JSON.stringify(originalData)));
    }
  };
  // 4. Clear/Wipe Function (Optional - sets everything to Absent/0)
  const clearMonth = () => {
    if (
      window.confirm("This will mark the entire month as ABSENT. Continue?")
    ) {
      const emptyData = {};
      daysInMonth.forEach((date) => {
        emptyData[date] = {
          status: "Absent",
          multiplier: 0,
          project_id: currentEmp?.assigned_site_id,
        };
      });
      setAttendanceData(emptyData);
    }
  };
  const currentEmp = employees.find((e) => e.id == selectedEmpId);

  return (
    <div className="space-y-8 p-4 animate-in fade-in duration-500">
      {/* Top Navigation */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-3xl font-black uppercase italic tracking-tighter text-slate-900">
            Attendance Ledger
          </h1>
          <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">
            Monthly Bulk Editor
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <select
            className="p-3 bg-white border rounded-xl font-black text-[10px] uppercase outline-none shadow-sm"
            value={selectedEmpId}
            onChange={(e) => setSelectedEmpId(e.target.value)}
          >
            <option value="">Select Employee...</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name} ({e.pay_type})
              </option>
            ))}
          </select>

          <input
            type="month"
            className="p-3 bg-white border rounded-xl font-black text-[10px] outline-none shadow-sm"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          />

          {selectedEmpId && (
            <button
              onClick={handleBulkSave}
              disabled={saving}
              className="bg-indigo-600 text-white px-8 py-3 rounded-xl font-black text-[10px] uppercase shadow-lg hover:bg-slate-900 transition-all flex items-center gap-2"
            >
              {saving ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Save size={14} />
              )}
              Save Monthly Log
            </button>
          )}
        </div>
      </div>
      {selectedEmpId && (
        <div className="bg-slate-900 p-6 rounded-[32px] mb-6 flex flex-wrap items-center gap-6 shadow-xl border border-slate-800 animate-in slide-in-from-top-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-500/20 rounded-lg text-indigo-400">
              <MapPin size={18} />
            </div>
            <div>
              <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest">
                Bulk Site
              </p>
              <select
                className="bg-transparent text-white font-black text-xs uppercase outline-none border-b border-slate-700 focus:border-indigo-500 transition-all"
                value={bulkSite}
                onChange={(e) => setBulkSite(e.target.value)}
              >
                <option value="" className="text-slate-900">
                  Select Site...
                </option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id} className="text-slate-900">
                    {p.project_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/20 rounded-lg text-emerald-400">
              <CheckCircle2 size={18} />
            </div>
            <div>
              <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest">
                Default Status
              </p>
              {currentEmp.pay_type === "Daily" ? (
                <select
                  className="bg-transparent text-white font-black text-xs uppercase outline-none border-b border-slate-700 focus:border-indigo-500"
                  value={bulkValue}
                  onChange={(e) => setBulkValue(e.target.value)}
                >
                  {[0, 0.5, 1, 1.2, 1.5, 2].map((v) => (
                    <option key={v} value={v} className="text-slate-900">
                      {v} Shift
                    </option>
                  ))}
                </select>
              ) : (
                <select
                  className="bg-transparent text-white font-black text-xs uppercase outline-none border-b border-slate-700 focus:border-indigo-500"
                  value={bulkValue}
                  onChange={(e) => setBulkValue(e.target.value)}
                >
                  <option value="1" className="text-slate-900">
                    Present (P)
                  </option>
                  <option value="0.5" className="text-slate-900">
                    Half-Day (HD)
                  </option>
                  <option value="0" className="text-slate-900">
                    Absent (A)
                  </option>
                </select>
              )}
            </div>
          </div>

          <div className="flex gap-2 ml-auto">
            <button
              onClick={revertToOriginal}
              className="bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white px-6 py-3 rounded-2xl font-black text-[10px] uppercase transition-all border border-slate-700"
            >
              Reset Changes
            </button>

            <button
              onClick={fillMonth}
              className="bg-indigo-500 hover:bg-white hover:text-indigo-600 text-white px-8 py-3 rounded-2xl font-black text-[10px] uppercase transition-all shadow-lg active:scale-95"
            >
              Fill Entire Month
            </button>
            <button
              onClick={clearMonth}
              className="bg-red-500 hover:bg-white hover:text-indigo-600 text-white px-8 py-3 rounded-2xl font-black text-[10px] uppercase transition-all shadow-lg active:scale-95"
            >
              Clear Entire Month
            </button>
          </div>
        </div>
      )}
      {loading ? (
        <div className="h-64 flex items-center justify-center font-black text-slate-300 uppercase animate-pulse">
          Fetching Calendar Data...
        </div>
      ) : selectedEmpId ? (
        <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest">
              <tr>
                <th className="p-6">Date</th>
                <th className="p-6">Assigned Site</th>
                <th className="p-6">Attendance / Work Log</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {daysInMonth.map((date) => {
                const data = attendanceData[date] || {
                  status: "Absent",
                  multiplier: 0,
                  project_id: currentEmp?.assigned_site_id,
                };
                const dayName = new Date(date).toLocaleDateString("en-US", {
                  weekday: "short",
                });

                return (
                  <tr
                    key={date}
                    className={`hover:bg-slate-50/50 transition-colors ${dayName === "Sun" ? "bg-rose-50/30" : ""}`}
                  >
                    <td className="p-6">
                      <p className="text-slate-900 font-black text-lg italic">
                        {date.split("-")[2]}
                      </p>
                      <p className="text-[12px] font-bold text-slate-400 uppercase">
                        {dayName}
                      </p>
                    </td>

                    <td className="p-6">
                      <div className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-2xl w-full max-w-xs">
                        <MapPin size={12} className="text-indigo-500" />
                        <select
                          className="bg-transparent border-none outline-none font-black text-[12px] uppercase w-full"
                          value={data.project_id || ""}
                          onChange={(e) =>
                            updateDayEntry(date, "project_id", e.target.value)
                          }
                        >
                          <option value="">Select Site...</option>
                          {projects.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.project_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>

                    <td className="p-6">
                      {/* Logic for DAILY: Custom Shift Multiplier */}
                      {currentEmp.pay_type === "Daily" && (
                        <select
                          className="p-2.5 bg-slate-900 text-white rounded-xl text-[12px] font-black uppercase outline-none"
                          value={data.multiplier}
                          onChange={(e) => {
                            updateDayEntry(date, "multiplier", e.target.value);
                            updateDayEntry(
                              date,
                              "status",
                              e.target.value > 0 ? "Present" : "Absent",
                            );
                          }}
                        >
                          {[0, 0.5, 1, 1.2, 1.5, 2, 2.5, 3].map((v) => (
                            <option key={v} value={v}>
                              {v} Shift
                            </option>
                          ))}
                        </select>
                      )}

                      {/* Logic for MONTHLY: Simple Status Buttons */}
                      {currentEmp.pay_type === "Monthly" && (
                        <div className="flex gap-1">
                          {[
                            { s: "Present", m: 1, label: "P" },
                            { s: "Absent", m: 0, label: "A" },
                            { s: "Half-day", m: 0.5, label: "HD" },
                          ].map((opt) => (
                            <button
                              key={opt.s}
                              onClick={() => {
                                updateDayEntry(date, "status", opt.s);
                                updateDayEntry(date, "multiplier", opt.m);
                              }}
                              className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase transition-all ${data.status === opt.s ? "bg-indigo-600 text-white shadow-md" : "bg-slate-100 text-slate-400"}`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Logic for HOURLY: Time In / Out */}
                      {currentEmp.pay_type === "Hourly" && (
                        <div className="flex gap-2 items-center">
                          <div className="flex items-center gap-2 bg-slate-50 p-1 px-3 rounded-xl border border-slate-100">
                            <Clock size={12} className="text-slate-400" />
                            <input
                              type="time"
                              className="bg-transparent p-1 text-[10px] font-bold outline-none"
                              value={data.in_time || ""}
                              onChange={(e) =>
                                updateDayEntry(date, "in_time", e.target.value)
                              }
                            />
                            <span className="text-slate-300">to</span>
                            <input
                              type="time"
                              className="bg-transparent p-1 text-[10px] font-bold outline-none"
                              value={data.out_time || ""}
                              onChange={(e) =>
                                updateDayEntry(date, "out_time", e.target.value)
                              }
                            />
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="h-64 border-4 border-dashed border-slate-100 rounded-[40px] flex flex-col items-center justify-center text-slate-200">
          <User size={48} className="mb-2 opacity-20" />
          <p className="font-black uppercase text-xs tracking-widest">
            Select an employee to start logging
          </p>
        </div>
      )}
    </div>
  );
};

export default MonthlyAttendanceEditor;
